import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, KeyRound, Sprout, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authService } from "@/services/authService";
import { apiError } from "@/lib/api";

export function EarlyAccessModal({ open, onOpenChange, redirectParams = "" }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [codeError, setCodeError] = useState("");

  const submitCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setCodeError("Enter your invite code"); return; }
    setChecking(true); setCodeError("");
    try {
      const res = await authService.validateCode(c);
      if (res.valid) {
        onOpenChange(false);
        const extra = redirectParams ? `&${redirectParams}` : "";
        navigate(`/register?code=${encodeURIComponent(c)}${extra}`);
      } else {
        setCodeError("That invite code isn't valid. Please check and try again.");
      }
    } catch (e) {
      setCodeError(apiError(e));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="early-access-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" /> Private Beta
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1" data-testid="early-access-message">
          Learn Me is currently in private beta. If you have an invite code, please enter it below to join the Dojo.
        </p>

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Invite code
          </label>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => { setCode(e.target.value); setCodeError(""); }}
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
              placeholder="Enter Promo Code" data-testid="early-access-code-input"
              className="rounded-none h-11 uppercase" autoFocus />
            <Button onClick={submitCode} disabled={checking} data-testid="early-access-code-submit"
              className="rounded-none h-11 px-4 gap-1.5 shrink-0">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sprout className="h-4 w-4" />}
              Join
            </Button>
          </div>
          {codeError && <p className="text-xs text-rose-600" data-testid="early-access-code-error">{codeError}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
